"use client";

import React, { useEffect, useState } from "react";
import { Terminal, ShieldCheck, Clock, Activity, Lock } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage, FormDescription } from "@/components/ui/form";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
function LiveClock() {
  const [time, setTime] = useState<Date | null>(null);
  useEffect(() => {
    setTime(new Date());
    const interval = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(interval);
  }, []);
  if (!time) {
    return <div className="font-mono text-4xl font-light tracking-widest text-white/50">
        00:00:00
      </div>;
  }
  return <div className="flex flex-col gap-1">
      <div className="font-mono text-4xl md:text-5xl font-light tracking-widest text-white drop-shadow-[0_0_8px_rgba(255,255,255,0.3)]">
        {time.toLocaleTimeString("en-US", {
        hour12: false
      })}
      </div>
      <div className="flex items-center gap-2 text-xs font-mono text-white/70 uppercase tracking-widest">
        <Activity className="size-3 text-white animate-pulse" />
        Global Server Time
      </div>
    </div>;
}
function TechnicalForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control
  } = useWvcForm();
  const [authCode, setAuthCode] = useState("");
  const isUnlocked = authCode.length === 4;
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center space-y-4 border border-white/30 bg-white/10 p-8 rounded-lg">
        <ShieldCheck className="size-12 text-white" />
        <h3 className="font-default text-xl text-white uppercase tracking-widest">
          Message Transmitted
        </h3>
        <p className="font-default text-sm text-white/90">
          {successMessage}
        </p>
      </div>;
  }
  return <Form onSubmit={handleSubmit}>
      {!isUnlocked ? <div className="flex flex-col items-center justify-center py-16 space-y-8 border border-white/20 bg-white/5 backdrop-blur-sm p-8 rounded-lg relative overflow-hidden group">
          <div className="absolute inset-0 bg-[linear-gradient(transparent_50%,rgba(255,255,255,0.05)_50%)] bg-[length:100%_4px] pointer-events-none" />
          
          <div className="flex flex-col items-center text-center space-y-2 z-10">
            <Lock className="size-8 text-white/70 mb-2" />
            <h3 className="font-default text-lg text-white uppercase tracking-widest">
              Verification Required
            </h3>
            <p className="font-default text-xs text-white/80">
              Enter the 4-digit security code to unlock the inquiry portal.
            </p>
          </div>

          <div className="z-10">
            <InputOTP maxLength={4} value={authCode} onChange={setAuthCode} containerClassName="gap-2">
              <InputOTPGroup>
                <InputOTPSlot index={0} className="font-mono text-lg border-white/40 text-white rounded-lg" />
                <InputOTPSlot index={1} className="font-mono text-lg border-white/40 text-white rounded-lg" />
                <InputOTPSlot index={2} className="font-mono text-lg border-white/40 text-white rounded-lg" />
                <InputOTPSlot index={3} className="font-mono text-lg border-white/40 text-white rounded-lg" />
              </InputOTPGroup>
            </InputOTP>
          </div>
          
          <div className="text-[10px] font-mono text-white/40 uppercase tracking-widest z-10">
            [ Security Protocol Active ]
          </div>
        </div> : <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-700">
          <div className="flex items-center justify-between border-b border-white/20 pb-4">
            <div className="flex items-center gap-2">
              <Terminal className="size-5 text-white" />
              <h3 className="font-default text-sm text-white uppercase tracking-widest">
                Inquiry Details
              </h3>
            </div>
            <div className="font-mono text-[10px] text-white/60 uppercase">
              Portal: Authorized
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <FormField control={control} name="name" rules={{
          required: "Name is required"
        }} render={({
          field
        }) => <FormItem>
                  <FormLabel className="font-default text-xs uppercase tracking-wider text-white/90">
                    Full Name *
                  </FormLabel>
                  <FormControl>
                    <input {...field} className="flex h-12 w-full rounded-lg border border-white/30 bg-white px-3 py-2 text-sm font-default text-black ring-offset-background placeholder:text-gray-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/50 transition-all duration-300" placeholder="Name" />
                  </FormControl>
                  <FormMessage className="font-default text-xs text-red-200" />
                </FormItem>} />

            <FormField control={control} name="email" rules={{
          required: "Email is required",
          pattern: {
            value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
            message: "Invalid email format"
          }
        }} render={({
          field
        }) => <FormItem>
                  <FormLabel className="font-default text-xs uppercase tracking-wider text-white/90">
                    Email Address *
                  </FormLabel>
                  <FormControl>
                    <input {...field} type="email" className="flex h-12 w-full rounded-lg border border-white/30 bg-white px-3 py-2 text-sm font-default text-black ring-offset-background placeholder:text-gray-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/50 transition-all duration-300" placeholder="Email" />
                  </FormControl>
                  <FormMessage className="font-default text-xs text-red-200" />
                </FormItem>} />

            <FormField control={control} name="company" render={({
          field
        }) => <FormItem>
                  <FormLabel className="font-default text-xs uppercase tracking-wider text-white/90">
                    Organization
                  </FormLabel>
                  <FormControl>
                    <input {...field} className="flex h-12 w-full rounded-lg border border-white/30 bg-white px-3 py-2 text-sm font-default text-black ring-offset-background placeholder:text-gray-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/50 transition-all duration-300" placeholder="Company Name" />
                  </FormControl>
                  <FormMessage className="font-default text-xs text-red-200" />
                </FormItem>} />

            <FormField control={control} name="phone" rules={{
          pattern: {
            value: /^[0-9+\-\s()]*$/,
            message: "Invalid phone format"
          }
        }} render={({
          field
        }) => <FormItem>
                  <FormLabel className="font-default text-xs uppercase tracking-wider text-white/90">
                    Contact Number
                  </FormLabel>
                  <FormControl>
                    <input {...field} type="tel" className="flex h-12 w-full rounded-lg border border-white/30 bg-white px-3 py-2 text-sm font-default text-black ring-offset-background placeholder:text-gray-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/50 transition-all duration-300" placeholder="+389..." />
                  </FormControl>
                  <FormMessage className="font-default text-xs text-red-200" />
                </FormItem>} />
          </div>

          <FormField control={control} name="topic" rules={{
        required: "Subject is required"
      }} render={({
        field
      }) => <FormItem>
                <FormLabel className="font-default text-xs uppercase tracking-wider text-white/90">
                  Inquiry Subject *
                </FormLabel>
                <FormControl>
                  <input {...field} className="flex h-12 w-full rounded-lg border border-white/30 bg-white px-3 py-2 text-sm font-default text-black ring-offset-background placeholder:text-gray-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/50 transition-all duration-300" placeholder="Subject" />
                </FormControl>
                <FormMessage className="font-default text-xs text-red-200" />
              </FormItem>} />

          <FormField control={control} name="message" rules={{
        required: "Message is required"
      }} render={({
        field
      }) => <FormItem>
                <FormLabel className="font-default text-xs uppercase tracking-wider text-white/90">
                  Your Message *
                </FormLabel>
                <FormControl>
                  <textarea {...field} className="flex min-h-[120px] w-full rounded-lg border border-white/30 bg-white px-3 py-2 text-sm font-default text-black ring-offset-background placeholder:text-gray-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/50 transition-all duration-300 resize-none" placeholder="Message" />
                </FormControl>
                <FormMessage className="font-default text-xs text-red-200" />
              </FormItem>} />

          {submitError && <div className="p-4 border border-red-400/50 bg-red-500/20 text-white font-default text-sm uppercase tracking-wider">
              Error: {submitError}
            </div>}

          <div className="flex items-center gap-4">
            <div className="font-default text-white text-sm whitespace-nowrap">
              11 + 13 =
            </div>
            <input className="w-16 h-12 rounded-lg border border-white/30 bg-white px-3 text-black text-sm" />
            <Button type="submit" disabled={isSubmitting} className="flex-1 rounded-lg font-default uppercase tracking-widest h-12 bg-primary text-white hover:bg-primary/90 shadow-lg hover:shadow-white/20 transition-all duration-300 relative overflow-hidden group">
              <span className="relative z-10 flex items-center gap-2">
                {isSubmitting ? <>
                    <Activity className="size-4 animate-spin" />
                    Sending...
                  </> : <>
                    Send
                  </>}
              </span>
              <div className="absolute inset-0 bg-[linear-gradient(90deg,transparent,rgba(255,255,255,0.2),transparent)] -translate-x-full group-hover:animate-[shimmer_1.5s_infinite]" />
            </Button>
          </div>
        </div>}
    </Form>;
}
export default function TheBriefingRoom() {
  return <section data-section-id="223533" id="global-inquiry-hub" className="relative bg-gradient-to-r from-[#007bff] to-[#0056b3] text-white py-24 lg:py-32 overflow-hidden">
      {}
      <div className="absolute inset-0 pointer-events-none opacity-10">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(255,255,255,0.1)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.1)_1px,transparent_1px)] bg-[size:4rem_4rem]" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8">
          
          {}
          <div className="lg:col-span-5 flex flex-col justify-between space-y-12">
            <div className="space-y-6">
              <div className="inline-flex items-center gap-2 px-3 py-1 border border-white/30 bg-white/10 text-white font-default text-xs uppercase tracking-widest rounded-lg">
                <Clock className="size-3" />
                <span>Global Reach</span>
              </div>
              
              <h2 className="font-default text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-white uppercase">
                Contact Us <br className="hidden lg:block" />
                <span className="text-white/80">For More Information</span>
              </h2>
              
              <div className="space-y-8 pt-4">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-white/10 rounded-lg">
                    <Activity className="size-6 text-white" />
                  </div>
                  <div>
                    <h4 className="font-default font-bold text-lg">Address</h4>
                    <p className="font-default text-white/80 text-sm leading-relaxed">Office Dept. 123 Pirotska&nbsp;Str.<br />1000 Sofia, Bulgaria</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="p-3 bg-white/10 rounded-lg">
                    <Activity className="size-6 text-white" />
                  </div>
                  <div>
                    <h4 className="font-default font-bold text-lg">Phone</h4>
                    <p className="font-default text-white/80 text-sm leading-relaxed">+359 2 3213 313&nbsp;</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="p-3 bg-white/10 rounded-lg">
                    <Activity className="size-6 text-white" />
                  </div>
                  <div>
                    <h4 className="font-default font-bold text-lg">Email</h4>
                    <p className="font-default text-white/80 text-sm leading-relaxed">klassentertainment@gmail.com</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="p-8 border border-white/20 bg-white/5 backdrop-blur-sm rounded-lg space-y-8 relative overflow-hidden">
              <div className="absolute top-0 left-0 w-2 h-full bg-white/20" />
              
              <LiveClock />

              <div className="space-y-4 pt-8 border-t border-white/20">
                <div className="flex justify-between items-center font-default text-xs">
                  <span className="text-white/70 uppercase tracking-widest">System Status</span>
                  <span className="text-white flex items-center gap-2">
                    <span className="size-2 rounded-full bg-green-400 animate-pulse" />
                    OPERATIONAL
                  </span>
                </div>
                <div className="flex justify-between items-center font-default text-xs">
                  <span className="text-white/70 uppercase tracking-widest">Security</span>
                  <span className="text-white">SSL ENCRYPTED</span>
                </div>
              </div>
            </div>
          </div>

          {}
          <div className="lg:col-span-7 lg:pl-12">
            <div className="border border-white/20 bg-white/10 backdrop-blur-md p-6 md:p-10 rounded-lg shadow-2xl shadow-black/20 relative">
              {}
              <div className="absolute top-0 left-0 w-4 h-4 border-t-2 border-l-2 border-white/50 -translate-x-1 -translate-y-1" />
              <div className="absolute top-0 right-0 w-4 h-4 border-t-2 border-r-2 border-white/50 translate-x-1 -translate-y-1" />
              <div className="absolute bottom-0 left-0 w-4 h-4 border-b-2 border-l-2 border-white/50 -translate-x-1 translate-y-1" />
              <div className="absolute bottom-0 right-0 w-4 h-4 border-b-2 border-r-2 border-white/50 translate-x-1 translate-y-1" />

              <div className="mb-8 border-b border-white/20 pb-6">
                <h3 className="font-default text-2xl font-bold text-white uppercase tracking-wide">
                  Send Us a Message
                </h3>
                <p className="font-default text-xs text-white/60 mt-2 uppercase tracking-widest">
                  Reference: ATL-INQ-2025
                </p>
              </div>

              <FormProvider formKey="global_inquiry_form_home" formId="global_inquiry_form_home" sectionName="global-inquiry-hub" defaultValues={{
              name: "",
              email: "",
              company: "",
              phone: "",
              topic: "",
              message: ""
            }} validationRules={{
              name: {
                required: true
              },
              email: {
                required: true,
                pattern: "email"
              },
              topic: {
                required: true
              },
              message: {
                required: true
              }
            }} submitText="Send" successMessage="Your inquiry has been received. Our team will contact you shortly.">
                <TechnicalForm />
              </FormProvider>
            </div>
          </div>

        </div>
      </div>
    </section>;
}